import { BlogPost } from './types';

export const mountainBikesPosts: BlogPost[] = [
  {
    id: 'mountain-bike-beginner-guide',
    title: 'The Complete Beginner\'s Guide to Mountain Biking',
    excerpt: 'Everything you need to know to start your mountain biking journey, from choosing your first bike to hitting the trails with confidence.',
    content: `

**The Complete Beginner's Guide to Mountain Biking**

Mountain biking is an exhilarating sport that combines adventure, fitness, and connection with nature. Whether you're looking to explore local trails or tackle challenging terrain, this guide will help you get started on the right foot.

**Choosing Your First Mountain Bike**

The type of mountain bike you choose depends on where and how you plan to ride:

**Cross Country (XC) Bikes**

- Lightweight and efficient
- Best for: Climbing, long-distance rides, racing
- Frame travel: 80-120mm

**Trail Bikes**

- Versatile all-around bikes
- Best for: Most trail riding, varied terrain
- Frame travel: 120-140mm

**Enduro Bikes**

- Built for aggressive downhill riding
- Best for: Steep descents, technical trails
- Frame travel: 140-170mm

**Downhill Bikes**

- Heavy-duty bikes for lift-served riding
- Best for: Bike parks, extreme downhill
- Frame travel: 180-220mm

**Essential Gear for Mountain Biking**

Safety should always be your top priority:

- Helmet: A properly fitted helmet is non-negotiable
- Gloves: Improve grip and protect your hands
- Eye protection: Sunglasses or goggles
- Appropriate clothing: Moisture-wicking and durable
- Shoes: Flat pedal shoes or clipless if using clipless pedals

**Basic Riding Techniques**

Body Position

- Keep your weight centered over the bike
- Bend your elbows and knees to absorb impact
- Look ahead, not down at the front wheel

Braking

- Use both brakes, but favor the rear brake
- Brake before corners, not during
- Modulate pressure to avoid skidding

Climbing

- Shift to easier gears before steep sections
- Keep your weight forward to maintain traction
- Maintain a steady cadence

Descending

- Shift your weight back and lower your center of gravity
- Let the bike move beneath you
- Use your arms and legs as suspension

**Trail Etiquette**

- Yield to hikers and horseback riders
- Announce yourself when passing: "On your left!"
- Stay on designated trails
- Pack out what you pack in
- Don't ride muddy trails

**Building Your Skills**

Start with easier trails and gradually progress. Consider taking a skills clinic or riding with more experienced friends. Practice in a safe environment before tackling challenging terrain.

Remember, mountain biking is about having fun and enjoying the outdoors. Take your time, be safe, and enjoy the ride!
    `,
    publishedAt: "2024-01-15",
    readTime: 8,
    category: "mountain bikes",
    tags: ["beginner", "guides", "technique", "safety", "mountain bikes"],
    author: "Chad G.",
    authorId: "chad-g",
    heroImage: "https://images.unsplash.com/photo-1472396961693-142e6e269027?auto=format&fit=crop&w=1200&q=80",
    thumbnail: "https://images.unsplash.com/photo-1472396961693-142e6e269027?auto=format&fit=crop&w=800&q=80"
  },
  {
    id: 'mountain-bike-maintenance-tips',
    title: 'Essential Mountain Bike Maintenance: Keep Your Ride Rolling',
    excerpt: 'Learn the basic maintenance tasks every mountain biker should know to keep their bike in top condition and extend its lifespan.',
    content: `

**Essential Mountain Bike Maintenance: Keep Your Ride Rolling**

Regular maintenance is crucial for mountain bike performance and safety. Here's what every rider should know about keeping their bike in top condition.

**Daily Pre-Ride Checks**

Before every ride, perform these quick checks:

**ABC Quick Check**

- Air: Check tire pressure and look for cuts or embedded objects
- Brakes: Test both brake levers for proper feel and stopping power
- Crank: Spin the pedals and check for unusual noises

**Additional Checks**

- Inspect the chain for proper lubrication
- Check that wheels are properly seated in dropouts
- Ensure quick-releases or thru-axles are properly tightened
- Test suspension movement (if applicable)

**Weekly Maintenance**

Chain Care
- Inspect chain for wear using a chain checker tool
- Clean the chain with degreaser and a chain tool
- Apply fresh chain lubricant appropriate for conditions
- Wipe excess lubricant to prevent dirt buildup

Brake Inspection

- Check brake pad wear
- Ensure brake cables move freely
- Test brake lever feel and adjust if necessary

Tire Inspection

- Look for cuts, punctures, or excessive wear
- Check tire pressure with a gauge
- Inspect tire sidewalls for cracks or damage

**Monthly Deep Clean**

Washing Your Bike

1. Use a bike-specific cleaner or mild soap
2. Avoid high-pressure water on bearings and suspension
3. Clean from top to bottom
4. Pay special attention to the drivetrain
5. Dry thoroughly and apply fresh lubricant

Drivetrain Maintenance

- Clean cassette and chainrings
- Check chain wear with a chain checker tool
- Inspect derailleur alignment and operation
- Lubricate pivot points

**Seasonal Maintenance**

Suspension Service

- Check air pressure and adjust sag
- Inspect seals for leaks or damage
- Consider professional service annually

Cable and Housing

- Replace worn or frayed cables
- Lubricate cable housing
- Adjust derailleur and brake cable tension

Bearing Inspection

- Check headset, bottom bracket, and wheel bearings
- Look for play or rough rotation
- Service or replace as needed

**When to See a Professional**

Some tasks require professional expertise:

- Suspension overhaul
- Hydraulic brake bleeding
- Wheel building or truing
- Bottom bracket service
- Internal cable routing

**Tools Every Mountain Biker Should Own**

Basic Tool Kit

- Multi-tool with hex keys and screwdrivers
- Tire levers and spare tubes
- Portable pump or CO2 inflator
- Chain tool
- Quick-link pliers

Home Workshop

- Floor pump with gauge
- Chain checker
- Torque wrench
- Bike stand
- Cleaning supplies and lubricants

**Conclusion**

Regular maintenance not only extends your bike's life but also ensures safer, more enjoyable rides. Start with basic tasks and gradually build your mechanical skills. When in doubt, consult your local bike shop professional.

A well-maintained mountain bike is a reliable companion for countless adventures on the trails!
    `,
    publishedAt: "2024-01-10",
    readTime: 10,
    category: "mountain bikes",
    tags: ["maintenance", "repair", "tools", "care", "mountain bikes", "guides"],
    author: "Chad G.",
    authorId: "chad-g",
    heroImage: "https://images.unsplash.com/photo-1534150034764-046bf225d3fa?auto=format&fit=crop&w=1200&q=80",
    thumbnail: "https://images.unsplash.com/photo-1534150034764-046bf225d3fa?auto=format&fit=crop&w=800&q=80"
  },
  {
    id: "best-mountain-bike-trails-la",
    title: "Top 10 Mountain Bike Trails Around Los Angeles",
    excerpt: "Discover the best mountain biking trails in and around LA, from beginner-friendly paths to challenging technical rides.",
    content: `

**Top 10 Mountain Bike Trails Around Los Angeles**

Los Angeles offers some of the best mountain biking in the world, with year-round riding weather and diverse terrain. Here are the top trails every LA mountain biker should experience.

**1. El Moro Canyon Trail (Crystal Cove State Park)**

- Distance: 8.2 miles
- Difficulty: Intermediate
- Highlights: Coastal views, smooth singletrack

This beautiful trail offers stunning ocean views and well-maintained singletrack through coastal sage scrub. The trail features rolling hills with moderate climbs and fun descents.

**2. Noble Canyon Trail (Mount Laguna)**

- Distance: 10 miles
- Difficulty: Advanced
- Highlights: Technical singletrack, forest riding

Located east of San Diego, this trail offers cooler temperatures and pine forest riding. Technical rock gardens and root sections challenge even experienced riders.

**3. Cheeseboro Canyon (Santa Monica Mountains)**

Distance: 6-12 miles (various loops)
Difficulty: Beginner to Intermediate
Highlights: Wide fire roads, gentle climbs

Perfect for beginners and families, Cheeseboro offers well-maintained fire roads with gentle grades and beautiful valley views.

**4. Mount Wilson via Gabrielino Trail**

- Distance: 14 miles
- Difficulty: Advanced
- Highlights: Historic railway, challenging climb

This epic climb follows the old Mount Wilson Railway and offers spectacular views of LA. The descent is technical and rewarding.

**5. Sullivan Canyon**

- Distance: 4.8 miles
- Difficulty: Beginner
- Highlights: Shaded canyon, easy access

A peaceful escape in the Santa Monica Mountains, Sullivan Canyon offers shaded riding through a beautiful riparian environment.

**6. Millard Canyon**

- Distance: 5.6 miles
- Difficulty: Intermediate
- Highlights: Waterfall views, varied terrain

This Angeles National Forest trail features creek crossings, technical sections, and views of Millard Falls.

**7. Whiting Ranch Wilderness Park**

- Distance: Various loops 2-8 miles
- Difficulty: Beginner to Advanced
- Highlights: Red rock formations, diverse trails

Multiple trail options ranging from easy fire roads to technical singletrack through unique red rock canyons.

**8. Aliso and Wood Canyons**

- Distance: 4-10 miles (various combinations)
- Difficulty: Beginner to Intermediate
- Highlights: Coastal proximity, well-marked trails

A network of trails near Laguna Beach offering everything from mellow family rides to challenging climbs.

**9. Ken Burton Trail (Griffith Park)**

- Distance: 3.2 miles
- Difficulty: Intermediate
- Highlights: City proximity, technical sections

Right in Los Angeles, this trail offers surprising technical challenges and great views of the city and Hollywood sign.

**10. Mount Lukens**

- Distance: 8.4 miles
- Difficulty: Advanced
- Highlights: Highest peak in LA, panoramic views

The climb to LA's highest peak is challenging but rewards riders with 360-degree views of the entire LA basin.

**Trail Etiquette and Safety**

- Always wear a helmet
- Yield to hikers and equestrians
- Stay on designated trails
- Check trail conditions before riding
- Bring plenty of water
- Ride with a buddy when possible
- Know your limits

**Best Times to Ride**

- Fall (October-November): Perfect weather, clear skies
- Winter (December-February): Cool temperatures, possible rain
- Spring (March-May): Mild weather, wildflowers
- Summer (June-September): Hot, start early or ride coastal trails

**Getting Started**

If you're new to mountain biking in LA, consider:

- Joining a local mountain bike group
- Taking a skills clinic
- Starting with easier trails and progressing gradually
- Renting gear before buying

Los Angeles mountain biking offers something for every rider. From beginner-friendly paths to world-class technical trails, there's always a new adventure waiting on the trails!
    `,
    publishedAt: "2024-01-05",
    readTime: 12,
    category: "mountain bikes",
    tags: ["trails", "los angeles", "guides", "locations", "mountain bikes"],
    author: "Chad G.",
    authorId: "chad-g",
    heroImage: "https://images.unsplash.com/photo-1615729947596-a598e5de0ab3?auto=format&fit=crop&w=1200&q=80",
    thumbnail: "https://images.unsplash.com/photo-1615729947596-a598e5de0ab3?auto=format&fit=crop&w=800&q=80"
  }
];
